import {
    CreateViewParams,
    UpdateView,
    viewContract,
} from "@common/contracts/view.contract";
import {
    isWorkspaceViewScope,
    NamedView,
    PersistedViewSchema,
    View,
    ViewScope,
    ViewType,
} from "@common/types/views/views";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { queryClient } from "../queryClient";

const viewContractClient = getBackendAPIClient(viewContract, undefined, {
    // For some reason this is not working properly, going to
    // do explicit parsing of the response body below and investigate
    // getting this working properly
    validateResponse: true,
});

interface UseViewsParams {
    scope: ViewScope;
    type?: ViewType;
}

interface PrefetchViewsParams {
    scope: ViewScope;
    type?: ViewType;
}

const getViewQueryKey = {
    all: () => ["views"],
    scoped: (scope: ViewScope) => [
        ...getViewQueryKey.all(),
        isWorkspaceViewScope(scope) ? scope.workspaceId : scope.organizationId,
    ],
    scopedWithType: (scope: ViewScope, type?: ViewType) => [
        ...getViewQueryKey.scoped(scope),
        type ?? "no-type",
    ],
} as const;

export function useViews(params: UseViewsParams) {
    const { scope, type } = params;

    return useQuery({
        queryKey: getViewQueryKey.scopedWithType(scope, type),
        queryFn: async () => {
            const response = await viewContractClient.getViews({
                query: {
                    type,
                    ...scope,
                },
            });

            if (response.status === 200) {
                return response.body.views.map((view) =>
                    PersistedViewSchema.parse(view),
                );
            }

            throw new Error("Failed to fetch views");
        },
        staleTime: 1000 * 30,
    });
}

export const prefetchViews = (params: PrefetchViewsParams): void => {
    const { scope, type } = params;

    queryClient.prefetchQuery({
        queryKey: getViewQueryKey.scopedWithType(scope, type),
        queryFn: async () => {
            const response = await viewContractClient.getViews({
                query: {
                    type,
                    ...scope,
                },
            });

            if (response.status === 200) {
                return response.body.views.map((view) =>
                    PersistedViewSchema.parse(view),
                );
            }

            throw new Error("Failed to fetch views");
        },
    });
};

export function useCreateViewMutation(scope: ViewScope) {
    return useMutation({
        mutationFn: async (data: CreateViewParams) => {
            const response = await viewContractClient.createView({
                body: {
                    ...data,
                    ...scope,
                },
            });

            if (response.status === 201) {
                return PersistedViewSchema.parse(response.body.view);
            }

            throw new Error("Failed to create view");
        },
        onSuccess: (newView) => {
            queryClient.invalidateQueries({
                queryKey: getViewQueryKey.scopedWithType(scope, newView.type),
            });
        },
    });
}

export function useUpdateViewMutation(scope: ViewScope) {
    return useMutation({
        mutationFn: async (data: UpdateView & { id: string }) => {
            // Optimistically update the cache
            queryClient.setQueryData(
                getViewQueryKey.scopedWithType(scope, data.type),
                (oldViews: Array<NamedView<View>> | undefined) => {
                    if (!oldViews) {
                        return oldViews;
                    }
                    return oldViews.map((view) =>
                        view.id === data.id ? { ...view, ...data } : view,
                    );
                },
            );

            const { id, ...updateData } = data;
            const response = await viewContractClient.updateView({
                params: { id },
                body: updateData,
            });

            if (response.status === 200) {
                return PersistedViewSchema.parse(response.body.view);
            }

            throw new Error("Failed to update view");
        },
        onSuccess: (updatedView) => {
            queryClient.invalidateQueries({
                queryKey: getViewQueryKey.scopedWithType(
                    scope,
                    updatedView.type,
                ),
            });
        },
    });
}

export function useDeleteViewMutation() {
    return useMutation({
        mutationFn: async (data: NamedView<View>) => {
            await viewContractClient.deleteView({
                params: { id: data.id },
            });

            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: getViewQueryKey.all(),
            });
        },
    });
}
